import { __decorate } from 'tslib';
import * as i0 from '@angular/core';
import { InjectionToken, inject, ElementRef, Directive, EventEmitter, Component, ChangeDetectionStrategy, Input, Output, DestroyRef, ChangeDetectorRef, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer } from '@angular/platform-browser';
import { WA_WINDOW } from '@ng-web-apis/common';
import { tuiPure, tuiIsPresent } from '@taiga-ui/cdk';
import * as i1 from '@taiga-ui/core';
import { TuiPositionAccessor, tuiAsPositionAccessor, TuiButton, TuiDropdown } from '@taiga-ui/core';
import { TUI_EDITOR_OPTIONS, TUI_EDITOR_RESIZE_EVENT, TUI_IMAGE_LOADER } from '@taiga-ui/editor/common';
import { AbstractTuiEditorResizable, TuiEditorResizable } from '@taiga-ui/editor/components';
import { timer, take, takeWhile } from 'rxjs';
import { TuiNodeView } from '@taiga-ui/editor/extensions/tiptap-node-view';
import { mergeAttributes } from '@tiptap/core';
import { Image } from '@tiptap/extension-image';
import { Plugin } from '@tiptap/pm/state';

/**
 * Size of resizable image inside editor
 */
const _c0 = ["resizable"];
const _c1 = ["dropdown"];
function TuiImageEditor_ng_template_7_Template(rf, ctx) {
  if (rf & 1) {
    const _r2 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "tui-image-align", 7);
    i0.ɵɵlistener("updateAlignStyles", function TuiImageEditor_ng_template_7_Template_tui_image_align_updateAlignStyles_0_listener($event) {
      i0.ɵɵrestoreView(_r2);
      const ctx_r2 = i0.ɵɵnextContext();
      ctx_r2.align($event);
      return i0.ɵɵresetView(ctx_r2.open = false);
    });
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵstyleMap(ctx_r2.style);
  }
}
const TUI_IMAGE_EDITOR_OPTIONS = new InjectionToken('[TUI_IMAGE_EDITOR_OPTIONS]', {
  factory: () => ({
    minWidth: null,
    maxWidth: Infinity
  })
});
let TuiImageOptionsPosition = /*#__PURE__*/(() => {
  class TuiImageOptionsPosition extends TuiPositionAccessor {
    constructor() {
      super(...arguments);
      this.el = inject(ElementRef);
      this.type = 'dropdown';
    }
    getPosition({
      width,
      height
    }) {
      const {
        right,
        top
      } = this.el.nativeElement.getBoundingClientRect();
      return [top + height + 5, right - width / 2];
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵTuiImageOptionsPosition_BaseFactory;
        return function TuiImageOptionsPosition_Factory(__ngFactoryType__) {
          return (ɵTuiImageOptionsPosition_BaseFactory || (ɵTuiImageOptionsPosition_BaseFactory = i0.ɵɵgetInheritedFactory(TuiImageOptionsPosition)))(__ngFactoryType__ || TuiImageOptionsPosition);
        };
      })();
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: TuiImageOptionsPosition,
        selectors: [["", "tuiImageOptionsPosition", ""]],
        features: [i0.ɵɵProvidersFeature([tuiAsPositionAccessor(TuiImageOptionsPosition)]), i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return TuiImageOptionsPosition;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let TuiImageAlignComponent = /*#__PURE__*/(() => {
  class TuiImageAlignComponent {
    constructor() {
      this.options = inject(TUI_EDITOR_OPTIONS);
      this.style = null;
      this.updateAlignStyles = new EventEmitter();
    }
    isAlignCenter(style) {
      return style?.replace(/\s/g, '')?.includes('justify-content:center') ?? false;
    }
    isAlignJustify(style) {
      return style === null || style === undefined || style === '';
    }
    isAlignLeft(style) {
      return style?.replace(/\s/g, '')?.includes('float:left') ?? false;
    }
    isAlignRight(style) {
      return style?.replace(/\s/g, '')?.includes('float:right') ?? false;
    }
    alignLeft() {
      this.updateAlignStyles.emit('float: left');
    }
    alignCenter() {
      this.updateAlignStyles.emit('display: flex; justify-content: center; margin-left: auto; margin-right: auto;');
    }
    alignJustify() {
      this.updateAlignStyles.emit(null);
    }
    alignRight() {
      this.updateAlignStyles.emit('float: right');
    }
    static {
      this.ɵfac = function TuiImageAlignComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TuiImageAlignComponent)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: TuiImageAlignComponent,
        selectors: [["tui-image-align"]],
        inputs: {
          style: "style"
        },
        outputs: {
          updateAlignStyles: "updateAlignStyles"
        },
        decls: 9,
        vars: 8,
        consts: [[1, "t-align-list"], ["size", "s", "title", "", "tuiIconButton", "", "type", "button", 3, "click.capture", "appearance", "iconStart"], ["size", "s", "tuiIconButton", "", "type", "button", 3, "click.capture", "appearance", "iconStart"]],
        template: function TuiImageAlignComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵelementStart(0, "div", 0)(1, "button", 1);
            i0.ɵɵlistener("click.capture", function TuiImageAlignComponent_Template_button_click_capture_1_listener() {
              return ctx.alignJustify();
            });
            i0.ɵɵtext(2, " Justify align ");
            i0.ɵɵelementEnd();
            i0.ɵɵelementStart(3, "button", 2);
            i0.ɵɵlistener("click.capture", function TuiImageAlignComponent_Template_button_click_capture_3_listener() {
              return ctx.alignLeft();
            });
            i0.ɵɵtext(4, " Left align ");
            i0.ɵɵelementEnd();
            i0.ɵɵelementStart(5, "button", 2);
            i0.ɵɵlistener("click.capture", function TuiImageAlignComponent_Template_button_click_capture_5_listener() {
              return ctx.alignCenter();
            });
            i0.ɵɵtext(6, " Center align ");
            i0.ɵɵelementEnd();
            i0.ɵɵelementStart(7, "button", 2);
            i0.ɵɵlistener("click.capture", function TuiImageAlignComponent_Template_button_click_capture_7_listener() {
              return ctx.alignRight();
            });
            i0.ɵɵtext(8, " Right align ");
            i0.ɵɵelementEnd()();
          }
          if (rf & 2) {
            i0.ɵɵadvance();
            i0.ɵɵproperty("appearance", ctx.isAlignJustify(ctx.style) ? "outline" : "flat")("iconStart", ctx.options.icons.imageExtension.alignJustify);
            i0.ɵɵadvance(2);
            i0.ɵɵproperty("appearance", ctx.isAlignLeft(ctx.style) ? "outline" : "flat")("iconStart", ctx.options.icons.imageExtension.alignLeft);
            i0.ɵɵadvance(2);
            i0.ɵɵproperty("appearance", ctx.isAlignCenter(ctx.style) ? "outline" : "flat")("iconStart", ctx.options.icons.imageExtension.alignCenter);
            i0.ɵɵadvance(2);
            i0.ɵɵproperty("appearance", ctx.isAlignRight(ctx.style) ? "outline" : "flat")("iconStart", ctx.options.icons.imageExtension.alignRight);
          }
        },
        dependencies: [TuiButton],
        styles: [".t-align-list[_ngcontent-%COMP%]{display:flex;gap:.3125rem;padding:.125rem}"],
        changeDetection: 0
      });
    }
  }
  __decorate([tuiPure], TuiImageAlignComponent.prototype, "isAlignCenter", null);
  __decorate([tuiPure], TuiImageAlignComponent.prototype, "isAlignJustify", null);
  __decorate([tuiPure], TuiImageAlignComponent.prototype, "isAlignLeft", null);
  __decorate([tuiPure], TuiImageAlignComponent.prototype, "isAlignRight", null);
  return TuiImageAlignComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let TuiImageEditor = /*#__PURE__*/(() => {
  class TuiImageEditor extends AbstractTuiEditorResizable {
    constructor() {
      super(...arguments);
      this.sanitizer = inject(DomSanitizer);
      this.el = inject(ElementRef);
      this.win = inject(WA_WINDOW);
      this.destroyRef = inject(DestroyRef);
      this.style = null;
      this.contenteditable = false;
      this.focused = false;
      this.open = false;
      this.options = inject(TUI_EDITOR_OPTIONS);
      this.imageOptions = inject(TUI_IMAGE_EDITOR_OPTIONS);
      this.changeDetector = inject(ChangeDetectorRef);
    }
    get height() {
      return null;
    }
    get minWidth() {
      return this.imageOptions.minWidth ?? 0;
    }
    get maxWidth() {
      return this.imageOptions.maxWidth ?? 0;
    }
    ngOnInit() {
      this.style = this.attrs.style;
    }
    ngAfterViewInit() {
      if (this.minWidth > 0) {
        this.updateMinWidth();
      }
    }
    updateSize([width]) {
      this.currentWidth = Math.max(this.minWidth, Math.min(this.maxWidth, width));
      this.attrs.width = this.currentWidth;
      this.notifyUpdate();
    }
    get dragHandle() {
      return this.attrs.draggable ?? null;
    }
    get alt() {
      return this.attrs.alt ?? '';
    }
    get title() {
      return this.attrs.title ?? '';
    }
    getBypassedSrc(src) {
      return this.sanitizer.bypassSecurityTrustResourceUrl(src);
    }
    currentTargetIsFocused(node) {
      this.focused = this.el.nativeElement.contains(node);
      if (this.focused) {
        this.selectFakeText();
      } else {
        this.open = false;
        this.dropdown?.toggle(false);
      }
    }
    openDropdown(event) {
      this.open = true;
      this.dropdown?.toggle(true);
      event.stopImmediatePropagation();
    }
    align(styles) {
      this.style = styles;
      this.attrs.style = styles;
      this.notifyUpdate();
    }
    selectFakeText() {
      if (this.win.document) {
        const range = this.win.document.createRange();
        this.el.nativeElement.querySelector('p')?.focus();
        range.selectNode(this.el.nativeElement);
        this.win.getSelection()?.removeAllRanges();
        this.win.getSelection()?.addRange(range);
      }
    }
    updateMinWidth() {
      timer(100).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        const naturalWidth = this.resizable?.container?.nativeElement.querySelector('img')?.naturalWidth ?? this.resizable?.width ?? this.attrs.width ?? 0;
        if (this.minWidth > parseInt(naturalWidth, 10)) {
          this.updateSize([this.minWidth]);
        }
      });
    }
    notifyUpdate() {
      this.el.nativeElement.dispatchEvent(new CustomEvent(TUI_EDITOR_RESIZE_EVENT, {
        bubbles: true
      }));
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵTuiImageEditor_BaseFactory;
        return function TuiImageEditor_Factory(__ngFactoryType__) {
          return (ɵTuiImageEditor_BaseFactory || (ɵTuiImageEditor_BaseFactory = i0.ɵɵgetInheritedFactory(TuiImageEditor)))(__ngFactoryType__ || TuiImageEditor);
        };
      })();
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: TuiImageEditor,
        selectors: [["tui-image-editor"]],
        viewQuery: function TuiImageEditor_Query(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵviewQuery(_c0, 7);
            i0.ɵɵviewQuery(_c1, 7);
          }
          if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.resizable = _t.first);
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.dropdown = _t.first);
          }
        },
        hostVars: 4,
        hostBindings: function TuiImageEditor_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵlistener("click.silent", function TuiImageEditor_click_silent_HostBindingHandler($event) {
              return ctx.currentTargetIsFocused($event.target);
            }, false, i0.ɵɵresolveDocument);
          }
          if (rf & 2) {
            i0.ɵɵattribute("data-drag-handle", ctx.dragHandle)("contenteditable", ctx.contenteditable);
            i0.ɵɵstyleMap(ctx.style);
          }
        },
        features: [i0.ɵɵInheritDefinitionFeature],
        decls: 9,
        vars: 17,
        consts: [["dropdown", "tuiDropdown"], ["resizable", ""], ["imgOptions", ""], ["tuiImageOptionsPosition", "", 1, "t-hosted", 3, "tuiDropdown", "tuiDropdownOpen"], ["appearance", "", "size", "s", "tuiIconButton", "", "type", "button", 1, "t-image-options", 3, "click.capture.prevent", "iconStart"], [3, "sizeChange", "autoHeight", "width"], ["contenteditable", "false", "decoding", "async", "loading", "lazy", 3, "alt", "src", "title"], [3, "updateAlignStyles"]],
        template: function TuiImageEditor_Template(rf, ctx) {
          if (rf & 1) {
            const _r1 = i0.ɵɵgetCurrentView();
            i0.ɵɵelementStart(0, "div", 3, 0)(2, "button", 4);
            i0.ɵɵlistener("click.capture.prevent", function TuiImageEditor_Template_button_click_capture_prevent_2_listener($event) {
              i0.ɵɵrestoreView(_r1);
              return i0.ɵɵresetView(ctx.openDropdown($event));
            });
            i0.ɵɵtext(3, " Image settings ");
            i0.ɵɵelementEnd();
            i0.ɵɵelementStart(4, "tui-editor-resizable", 5, 1);
            i0.ɵɵlistener("sizeChange", function TuiImageEditor_Template_tui_editor_resizable_sizeChange_4_listener($event) {
              i0.ɵɵrestoreView(_r1);
              return i0.ɵɵresetView(ctx.updateSize($event));
            });
            i0.ɵɵelement(6, "img", 6);
            i0.ɵɵelementEnd()();
            i0.ɵɵtemplate(7, TuiImageEditor_ng_template_7_Template, 1, 2, "ng-template", null, 2, i0.ɵɵtemplateRefExtractor);
          }
          if (rf & 2) {
            const imgOptions_r4 = i0.ɵɵreference(8);
            i0.ɵɵproperty("tuiDropdown", imgOptions_r4)("tuiDropdownOpen", ctx.open);
            i0.ɵɵadvance(2);
            i0.ɵɵclassProp("_open", ctx.open);
            i0.ɵɵproperty("iconStart", ctx.options.icons.imageExtension.settings);
            i0.ɵɵadvance(2);
            i0.ɵɵproperty("autoHeight", true)("width", ctx.width);
            i0.ɵɵadvance(2);
            i0.ɵɵstyleProp("max-width", ctx.maxWidth, "px")("min-width", ctx.minWidth, "px");
            i0.ɵɵclassProp("ProseMirror-selectednode", ctx.focused);
            i0.ɵɵproperty("alt", ctx.alt)("src", ctx.getBypassedSrc(ctx.attrs.src), i0.ɵɵsanitizeUrl)("title", ctx.title);
            i0.ɵɵattribute("width", ctx.width);
          }
        },
        dependencies: [TuiButton, i1.TuiDropdownDirective, i1.TuiDropdownOpen, TuiEditorResizable, TuiImageAlignComponent, TuiImageOptionsPosition],
        styles: ["[_nghost-%COMP%]{display:inline-block}[data-drag-handle][_nghost-%COMP%]{cursor:move}[_nghost-%COMP%]:hover   .t-image-options[_ngcontent-%COMP%]{opacity:1}img[_ngcontent-%COMP%]{pointer-events:none}.t-hosted[_ngcontent-%COMP%]{position:relative}.t-image-options[_ngcontent-%COMP%]{position:absolute;top:.625rem;right:.625rem;z-index:1;background:var(--tui-background-base)}.t-image-options[_ngcontent-%COMP%]:not(._open){transition-property:opacity;transition-duration:var(--tui-duration, .3s);transition-timing-function:ease-in-out;opacity:0}.t-align-list[_ngcontent-%COMP%]{display:flex;gap:.3125rem;padding:.125rem}"],
        changeDetection: 0
      });
    }
  }
  __decorate([tuiPure], TuiImageEditor.prototype, "getBypassedSrc", null);
  return TuiImageEditor;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function pasteImage(injector) {
  return (view, event) => {
    const dataTransfer = event instanceof DragEvent ? event.dataTransfer : event.clipboardData;
    const imagesFiles = Array.from(dataTransfer?.files ?? []).filter(file => /image/i.test(file.type));
    if (imagesFiles.length) {
      event.preventDefault();
    }
    const loader = injector.get(TUI_IMAGE_LOADER);
    imagesFiles.forEach(file => {
      loader(file).pipe(take(1), takeWhile(() => !view.isDestroyed)).subscribe(src => {
        const node = view.state.schema.nodes.image?.create({
          src
        });
        const transaction = node ? view.state.tr.replaceSelectionWith(node) : null;
        setTimeout?.(() => {
          if (!view.isDestroyed && transaction) {
            view.dispatch(transaction);
          }
        });
      });
    });
  };
}
function tuiCreateImageEditorExtension({
  injector,
  draggable
}) {
  const enableDraggable = tuiIsPresent(draggable) ? draggable : true;
  return Image.extend({
    name: 'image',
    group: 'inline',
    inline: true,
    priority: 0,
    selectable: true,
    draggable: enableDraggable,
    parseHTML() {
      return [{
        tag: 'img'
      }];
    },
    addAttributes() {
      return {
        src: {
          default: '',
          keepOnSplit: false
        },
        width: {
          default: null,
          keepOnSplit: false
        },
        alt: {
          default: '',
          keepOnSplit: false
        },
        style: {
          default: '',
          keepOnSplit: false
        },
        title: {
          default: '',
          keepOnSplit: false
        },
        draggable: {
          default: enableDraggable ? '' : null,
          keepOnSplit: false
        }
      };
    },
    renderHTML({
      HTMLAttributes
    }) {
      return ['img', mergeAttributes(HTMLAttributes)];
    },
    addNodeView() {
      return props => new TuiNodeView(TuiImageEditor, props, {
        injector,
        ...props
      });
    },
    addCommands() {
      return {
        setEditableImage: attrs => ({
          commands
        }) => commands.insertContent({
          type: this.name,
          attrs
        })
      };
    },
    addProseMirrorPlugins() {
      return [new Plugin({
        props: {
          handleDOMEvents: {
            paste: pasteImage(injector),
            drop: pasteImage(injector)
          }
        }
      })];
    }
  });
}
const setup = tuiCreateImageEditorExtension;

/**
 * Generated bundle index. Do not edit.
 */

export { TUI_IMAGE_EDITOR_OPTIONS, TuiImageEditor, TuiImageOptionsPosition, setup, tuiCreateImageEditorExtension };
